import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { usersApi } from '@/features/users/api/usersApi';
import { workspacesApi } from '@/features/workspaces/api/workspacesApi';
import { User } from '@web/types';

interface AuthState {
  currentUser: Omit<User, 'id'> | null;
  currentWorkspaceSlug: string | null;
  isWorkspaceSetupRequired: boolean | null;
  isWorkspaceOnboarded: boolean | null;
}

const initialState: AuthState = {
  currentUser: null,
  currentWorkspaceSlug: null,
  isWorkspaceSetupRequired: null,
  isWorkspaceOnboarded: null,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthData: (
      state,
      action: PayloadAction<{
        currentUser: Omit<User, 'id'> | null;
        currentWorkspaceSlug: string | null;
        isWorkspaceSetupRequired: boolean | null;
        isWorkspaceOnboarded: boolean | null;
      }>,
    ) => {
      state.currentUser = action.payload.currentUser;
      state.currentWorkspaceSlug = action.payload.currentWorkspaceSlug;
      state.isWorkspaceSetupRequired = action.payload.isWorkspaceSetupRequired;
      state.isWorkspaceOnboarded = action.payload.isWorkspaceOnboarded;
    },
    clearAuth: (state) => {
      state.currentUser = null;
      state.currentWorkspaceSlug = null;
      state.isWorkspaceSetupRequired = null;
      state.isWorkspaceOnboarded = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        workspacesApi.endpoints.acceptInvite.matchFulfilled,
        (state, action) => {
          const data = (action.payload as any)?.data;
          const user = data?.user;
          const workspace = data?.workspace;

          if (user) {
            const { id: _id, ...rest } = user;
            state.currentUser = rest as Omit<User, 'id'>;
          }

          state.currentWorkspaceSlug = workspace?.slug ?? null;
          state.isWorkspaceSetupRequired =
            data?.is_workspace_setup_required ?? null;
          state.isWorkspaceOnboarded =
            workspace?.is_workspace_onboarded ?? null;
        },
      )
      .addMatcher(
        usersApi.endpoints.patchUserById.matchFulfilled,
        (state, action) => {
          const updatedUser = (action.payload as any)?.data;
          if (!updatedUser) {
            return;
          }

          const { id: _id, ...rest } = updatedUser;
          state.currentUser = {
            ...(state.currentUser ?? {}),
            ...rest,
          } as Omit<User, 'id'>;
        },
      );
  },
});

export const { setAuthData, clearAuth } = authSlice.actions;
export default authSlice.reducer;
